import { faChevronCircleLeft } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type {
  Hash,
  LocationDescriptor,
  LocationKey,
  Pathname,
  Search,
} from 'history';
import { useCallback, useMemo } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { ExternalLink, Link } from 'src/components/navigation/link';
import { setLocationHref } from 'src/utils/location-usage';

type ReferrerLocation = {
  pathname: Pathname;
  search?: Search;
  state?: WithOptionalReferrer;
  hash?: Hash;
  key?: LocationKey;
};

export type ReferrerState = {
  location: ReferrerLocation | string;
  label: string;
  parent?: ReferrerState;
};

export type Referrer = ReferrerState & {
  backLink: JSX.Element;
  cancelLink: JSX.Element;
  goTo: () => void;
  attachTo: (
    location: LocationDescriptor<{}>
  ) => LocationDescriptor<{ referrer: ReferrerState }>;
};

export type SavedReferrer = ReferrerState & {
  attachTo: (
    location: LocationDescriptor<{}>
  ) => LocationDescriptor<{ referrer: ReferrerState }>;
};

type WithOptionalReferrer = { referrer?: ReferrerState } | undefined;

/**
 * Imagine the following use case:
 * - The user is on **Page A** which shows a table which can be filtered.
 * - The user sets some filters and is now on **Page A with search params**.
 * - The user clicks on an item an comes to **Page B**.
 * - On **Page B** is a "Back to Page A" link which should take you back to **Page A**,
 * but _with_ the previously configured search params.
 * - On **Page B** you can also select another action which gets you to **Page C**.
 * - On **Page C** is a "Back to Page B" link which should take you back to **Page B**.
 * - **Page C** also contains a form which will take you back to **Page B**, if you
 * cancel or submit the form as well.
 * - If the user is back on **Page B** and now clicks on "Back to Page A" we'll get back
 * to Page A, but _with_ the previously configured search params.
 *
 * Imagine the additional problem that you can also get to **Page B** from **Page D**
 * and that the back link on **Page B** should now say "Back to Page D" and _not_
 * "Back to Page A".
 *
 * That's what the hooks `useSaveReferrer()` and `useReferrer()` try to solve.
 * - Via `useSaveReferrer()` you can keep track of your current referrer, give it a label
 * and also keep track of parent page referrers.
 * - Via `useReferrer()` you can access a referrer and set a default, if needed.
 *
 * It works with `<Link/>`, `<Redirect/>`, `history.push` - anything which takes a `LocationDescriptor`.
 *
 * If you're interested in a real world example:
 * - Page A is "My Transactions"
 * - Page B is "Allocation to Balancing Groups" (for trades or deals)
 * - Page C is "Add/Edit Allocation to Balancing Groups"
 * - Page D is "Portfolio Overview"
 *
 * **Note**: This does not mean that you have to use referrers now _everywhere_. From _any_ page
 * to _any_ page. This is only about pages with one common user flow and explicit parent-child-relationships
 * of pages. This could be "Overview <-> Detail" pages or "Detail <-> Edit" pages for example.
 */
export function useReferrer(defaultReferrer: ReferrerState): Referrer {
  const { state } = useLocation<WithOptionalReferrer>();
  const referrerState = state?.referrer ?? defaultReferrer;

  // additional span is used, so it can be safely used as a child of <Stack flow="row"/>
  // without making the link go over the whole page
  const backLink = (
    <span>
      {isExternal(referrerState.location) ? (
        <ExternalLink href={referrerState.location}>
          <FontAwesomeIcon icon={faChevronCircleLeft} aria-hidden /> Back to{' '}
          {referrerState.label}
        </ExternalLink>
      ) : (
        <Link to={referrerState.location} data-testid="referrer-back-link">
          <FontAwesomeIcon icon={faChevronCircleLeft} aria-hidden /> Back to{' '}
          {referrerState.label}
        </Link>
      )}
    </span>
  );

  const cancelLink = isExternal(referrerState.location) ? (
    <ExternalLink href={referrerState.location} mode="button-secondary">
      Cancel
    </ExternalLink>
  ) : (
    <Link to={referrerState.location} mode="button-secondary">
      Cancel
    </Link>
  );

  const { push } = useHistory();

  const goTo = () => {
    if (isExternal(referrerState.location)) {
      setLocationHref(referrerState.location);
    } else {
      push(referrerState.location);
    }
  };

  const attachTo = useCallback(
    (location: LocationDescriptor<{}>) => {
      if (typeof location === 'string') {
        return {
          pathname: location,
          state: { referrer: referrerState },
        };
      } else {
        return {
          ...location,
          state: { ...location.state, referrer: referrerState },
        };
      }
    },
    [referrerState]
  );

  return { ...referrerState, backLink, cancelLink, goTo, attachTo };
}

type UseSaveReferrerOptions = {
  label: string;
  // TODO: Add support for anchor in a follow-up merge request.
  // anchor: string;
};

export function useSaveReferrer({
  label,
}: UseSaveReferrerOptions): SavedReferrer {
  const location = useLocation<WithOptionalReferrer>();

  const referrerState = useMemo<ReferrerState>(
    () => ({
      location,
      label,
      parent: location.state?.referrer,
    }),
    [label, location]
  );

  const attachTo = useCallback(
    (location: LocationDescriptor<{}>) => {
      if (typeof location === 'string') {
        return {
          pathname: location,
          state: { referrer: referrerState },
        };
      } else {
        return {
          ...location,
          state: { ...location.state, referrer: referrerState },
        };
      }
    },
    [referrerState]
  );

  return { ...referrerState, attachTo };
}

function isExternal(location: ReferrerLocation | string): location is string {
  return (
    typeof location === 'string' &&
    (location.startsWith('https://') || location.startsWith('http://'))
  );
}
